import { computed, ref, Ref } from 'vue';
import { ICheckboxEvent } from '@/models/input-events.interface';
import { IPresentableEntity } from '@/models/available-entities.interface';
import { LoadingStatuses } from '@/models/loading.interface';
import { DegreeLevelPresenter } from '@studyportals/campaign-management-api-interface';
import entityNamePresenter from '@/presenters/entity-name-presenter';
import entityPremiumnessPresenter from '@/presenters/entity-premiumness-presenter';
import entityVisibilityPresenter from '@/presenters/entity-visibility-presenter';
import entityAttachingPresenter from '@/presenters/entity-attaching-presenter';
import datePresenter from'@/presenters/date-presenter';
import store from '@/store';

export default class TableEntitiesCode {
	private lastClickedEntityId: Ref<string | number | null> = ref(null);
	private degreeLevelPresenter: DegreeLevelPresenter;

	public isLoading = computed((): boolean => {
		const loadingStatus =  store.getters.loadingStatusEntities();
		return loadingStatus === LoadingStatuses.ACTIVE;
	});

	public areAllEntitiesAttached = computed((): boolean => {
		return this.filteredEntities.value.every((entity: IPresentableEntity) => entity.attached);
	});

	public checkboxTitle = computed((): string => {
		if (!this.hasCampaignFinished.value) {
			return '';
		}

		return 'This campaign has ended. You can no longer attach or detach studies or organisations.';
	});

	public hasCampaignFinished = computed((): boolean => {
		if (!this.endDate.value) {
			return false;
		}

		return datePresenter.isDateInThePast(this.endDate.value);
	});

	public filteredEntities = computed((): IPresentableEntity[] => {
		return store.getters.filteredEntities();
	});

	private endDate = computed((): Date | null => {
		return store.getters.endDate();
	});

	constructor() {
		this.degreeLevelPresenter = new DegreeLevelPresenter();
	}

	public onAllEntitiesCheckboxClicked(event: ICheckboxEvent): void {
		const checked = event?.target?.checked;
		if (checked === undefined) {
			return;
		}

		store.mutations.setAttachedStatusOfAllEntities(checked);
	}

	public onEntityAttachedStatusChanged(event: { shiftKey: boolean }, entity: IPresentableEntity): void {
		if (event.shiftKey && this.tryToProcessEntityClickAsShiftClick(entity)) {
			return;
		}

		this.lastClickedEntityId.value = entity.id;
		store.mutations.setAttachedStatusOfEntity(
			entity.id,
			!entity.attached
		);
	}
	public toggleVisibilityOfChildrenOfEntity(entity: IPresentableEntity): void {
		if (!entity.hasChildren) {
			return;
		}

		store.mutations.setVisibilityOfChildrenOfEntity(entity.id, !entity.hideChildren);
	}

	public isParentHidden(entity: IPresentableEntity): boolean {
		return entityVisibilityPresenter.isParentHidden(entity);
	}

	public retrieveNameStylingClassNameForEntity(entity: IPresentableEntity): string {
		return entityNamePresenter.getStylingClassName(entity);
	}

	public retrieveDegreeLevelFullNameForEntity(entity: IPresentableEntity): string {
		return this.degreeLevelPresenter.getFullName(entity.degreeLevel);
	}

	public retrieveDegreeLevelAbbreviatedNameForEntity(entity: IPresentableEntity): string {
		return this.degreeLevelPresenter.getAbbreviatedName(entity.degreeLevel);
	}

	public retrievePremiumnessFullNameForEntity(entity: IPresentableEntity): string {
		return entityPremiumnessPresenter.getFullName(entity);
	}

	public retrievePremiumnessAbbreviatedNameForEntity(entity: IPresentableEntity): string {
		return entityPremiumnessPresenter.getAbbreviatedName(entity);
	}

	public retrievePremiumnessStylingClassNameForEntity(entity: IPresentableEntity): string {
		return entityPremiumnessPresenter.getStylingClassName(entity);
	}

	private tryToProcessEntityClickAsShiftClick(clickedEntity: IPresentableEntity): boolean {
		// If no previous checkbox was clicked, bail out.
		if (!this.lastClickedEntityId.value) {
			return false;
		}

		entityAttachingPresenter.handleShiftClick(clickedEntity, this.lastClickedEntityId.value, !clickedEntity.attached);
		return true;
	}
}
